import { Plate } from '../components/Card';
import React, { useEffect, useState, useCallback } from 'react';
import api from '../api';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  Cell, PieChart, Pie, Legend,
} from 'recharts';

const fmt = n =>
  n ? n.toLocaleString('ru-RU', { maximumFractionDigits: 0 }) + ' ₽' : '0 ₽';
const fmtNum = n =>
  typeof n === 'number' ? n.toLocaleString('ru-RU') : n;

// ─── helpers ──────────────────────────────────────────────────────────────────
const STATUS_PRIORITY = { '1': 'Обычный', '2': 'Высокий' };
const TASK_STATUS = {
  '1': 'Ждёт',
  '2': 'В работе',
  '3': 'Завершена',
  '4': 'Отложена',
  '5': 'Отклонена',
};

function daysDiff(dateStr) {
  if (!dateStr) return null;
  const diff = Math.ceil((new Date(dateStr) - new Date()) / 86400000);
  return diff;
}

// ─── Tab button ──────────────────────────────────────────────────────────────
function Tab({ label, active, badge, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '9px 18px',
        borderRadius: 8,
        border: 'none',
        cursor: 'pointer',
        fontWeight: active ? 700 : 500,
        fontSize: 13,
        background: active ? 'var(--accent)' : 'var(--bg2)',
        color: active ? '#fff' : 'var(--text1)',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        transition: 'all 0.18s',
        position: 'relative',
      }}
    >
      {label}
      {badge > 0 && (
        <span style={{
          background: active ? 'rgba(30,36,37,0.25)' : '#e04848',
          color: '#fff',
          borderRadius: 10,
          fontSize: 11,
          fontWeight: 700,
          padding: '1px 6px',
          minWidth: 18,
          textAlign: 'center',
        }}>{badge}</span>
      )}
    </button>
  );
}

// ─── Pipeline card ────────────────────────────────────────────────────────────
function PipelineCard({ pipeline, onClick, active }) {
  const maxCount = Math.max(...(pipeline.stages || []).map(s => s.count), 1);
  return (
    <div
      onClick={onClick}
      style={{
        background: active ? 'var(--bg2)' : 'var(--bg2)',
        border: `2px solid ${active ? pipeline.color : 'var(--border)'}`,
        borderRadius: 14,
        padding: '18px 20px',
        cursor: 'pointer',
        transition: 'all 0.18s',
        boxShadow: active ? `0 0 0 3px ${pipeline.color}22` : 'none',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <span style={{
          fontSize: 24, lineHeight: 1,
          background: `${pipeline.color}22`,
          borderRadius: 8,
          padding: '5px 8px',
        }}>{pipeline.icon}</span>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text)' }}>
            {pipeline.name}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text2)' }}>
            {fmtNum(pipeline.total_open)} сделок открыто
          </div>
        </div>
        <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: pipeline.color }}>
            {fmt(pipeline.total_amount)}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text2)' }}>в выборке</div>
        </div>
      </div>

      {/* Mini funnel bars */}
      {pipeline.stages && pipeline.stages.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {pipeline.stages.slice(0, 6).map(s => (
            <div key={s.stage_id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{
                fontSize: 11,
                color: 'var(--text2)',
                width: 130,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}>{s.name}</div>
              <div style={{ flex: 1, background: 'var(--bg3)', borderRadius: 3, height: 6 }}>
                <div style={{
                  width: `${Math.round((s.count / maxCount) * 100)}%`,
                  height: '100%',
                  background: pipeline.color,
                  borderRadius: 3,
                  minWidth: s.count > 0 ? 4 : 0,
                }} />
              </div>
              <div style={{ fontSize: 11, color: 'var(--text1)', width: 28, textAlign: 'right' }}>
                {s.count}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Funnel detail ────────────────────────────────────────────────────────────
function PipelineDetail({ pipeline }) {
  if (!pipeline) return null;
  const data = (pipeline.stages || []).map(s => ({
    name: s.name.length > 18 ? s.name.slice(0, 18) + '…' : s.name,
    fullName: s.name,
    count: s.count,
    amount: s.amount,
  }));
  return (
    <div style={{
      background: 'var(--bg2)',
      border: `1px solid ${pipeline.color}44`,
      borderRadius: 14,
      padding: '20px 24px',
      marginTop: 0,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <span style={{ fontSize: 28 }}>{pipeline.icon}</span>
        <div>
          <h3 style={{ margin: 0, color: pipeline.color, fontSize: 18 }}>{pipeline.name}</h3>
          <span style={{ color: 'var(--text2)', fontSize: 13 }}>
            Всего открытых: <b style={{ color: 'var(--text)' }}>{fmtNum(pipeline.total_open)}</b>
            &nbsp;·&nbsp;Сумма выборки: <b style={{ color: pipeline.color }}>{fmt(pipeline.total_amount)}</b>
          </span>
        </div>
      </div>

      {data.length > 0 ? (
        <>
          <div style={{ height: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} layout="vertical" margin={{ left: 10, right: 40, top: 0, bottom: 0 }}>
                <XAxis type="number" tick={{ fill: 'var(--text2)', fontSize: 11 }} />
                <YAxis type="category" dataKey="name" width={140} tick={{ fill: 'var(--text1)', fontSize: 11 }} />
                <Tooltip
                  contentStyle={{ background: 'var(--bg3)', border: `1px solid ${pipeline.color}44`, borderRadius: 8, color: 'var(--text)' }}
                  formatter={(v, n, p) => [`${v} сделок · ${fmt(p.payload.amount)}`, 'Стадия']}
                />
                <Bar dataKey="count" radius={[0, 4, 4, 0]} fill={pipeline.color} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 16 }}>
            {pipeline.stages.map(s => (
              <div key={s.stage_id} style={{
                background: 'var(--bg3)',
                borderRadius: 8,
                padding: '8px 12px',
                minWidth: 120,
              }}>
                <div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 2 }}>{s.name}</div>
                <div style={{ fontWeight: 700, color: pipeline.color, fontSize: 16 }}>{s.count}</div>
                <div style={{ fontSize: 11, color: 'var(--text2)' }}>{fmt(s.amount)}</div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div style={{ color: 'var(--text2)', textAlign: 'center', padding: 30 }}>
          Стадии не загружены. Данные обновятся после синхронизации.
        </div>
      )}
    </div>
  );
}

// ─── Tasks tab ────────────────────────────────────────────────────────────────
function TasksTab({ tasks }) {
  if (!tasks.length) return (
    <div style={{ textAlign: 'center', padding: 40, color: 'var(--text2)' }}>
      Нет активных задач
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {tasks.map(t => {
        const days = daysDiff(t.deadline);
        const isOverdue = days !== null && days < 0;
        const isUrgent = days !== null && days <= 2 && days >= 0;
        return (
          <div key={t.id} style={{
            background: 'var(--bg2)',
            borderRadius: 10,
            padding: '12px 16px',
            border: `1px solid ${isOverdue ? '#e0484844' : isUrgent ? '#e08a0044' : 'var(--border)'}`,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
          }}>
            <div style={{
              width: 8, height: 8, borderRadius: 50,
              background: isOverdue ? '#e04848' : isUrgent ? '#e08a00' : t.priority === '2' ? '#00a7dc' : 'var(--text2)',
              marginTop: 5, flexShrink: 0,
            }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)' }}>{t.title}</div>
              {t.description && (
                <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 3 }}>{t.description}</div>
              )}
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              {t.deadline && (
                <div style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: isOverdue ? '#e04848' : isUrgent ? '#e08a00' : 'var(--text2)',
                }}>
                  {isOverdue ? `⚠ просрочено ${Math.abs(days)}д` : `📅 ${t.deadline}`}
                </div>
              )}
              <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2 }}>
                {TASK_STATUS[t.status] || t.status}
                {t.priority === '2' && <span style={{ color: '#00a7dc', marginLeft: 6 }}>● Высокий</span>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Chats tab ────────────────────────────────────────────────────────────────
function ChatsTab({ chats }) {
  const totalUnread = chats.reduce((s, c) => s + (c.unread || 0), 0);
  if (!chats.length) return (
    <div style={{ textAlign: 'center', padding: 40, color: 'var(--text2)' }}>
      Нет чатов
    </div>
  );

  return (
    <div>
      {totalUnread > 0 && (
        <div style={{
          background: '#e0484822',
          border: '1px solid #e0484844',
          borderRadius: 10,
          padding: '10px 14px',
          marginBottom: 14,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          color: '#e04848',
          fontWeight: 600,
          fontSize: 13,
        }}>
          🔔 Непрочитано: {totalUnread} сообщений
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {chats.map(c => (
          <div key={c.id} style={{
            background: 'var(--bg2)',
            borderRadius: 10,
            padding: '12px 16px',
            border: `1px solid ${c.unread > 0 ? 'var(--accent)44' : 'var(--border)'}`,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}>
            <div style={{
              width: 40, height: 40, borderRadius: 20,
              background: c.unread > 0 ? 'var(--accent)22' : 'var(--bg3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 18, flexShrink: 0,
            }}>
              {c.type === 'chat' ? '💬' : c.type === 'channel' ? '📢' : '👤'}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontWeight: c.unread > 0 ? 700 : 500,
                fontSize: 14,
                color: 'var(--text)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>{c.title || 'Без названия'}</div>
              {c.message && (
                <div style={{
                  fontSize: 12, color: 'var(--text2)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  marginTop: 2,
                }}>{c.message}</div>
              )}
            </div>
            {c.unread > 0 && (
              <div style={{
                background: 'var(--accent)',
                color: '#fff',
                borderRadius: 12,
                padding: '2px 8px',
                fontSize: 12,
                fontWeight: 700,
                flexShrink: 0,
              }}>{c.unread}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Leads / Deals simple view ────────────────────────────────────────────────
function SourceBar({ items, color }) {
  const max = Math.max(...items.map(i => i.count), 1);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {items.map(i => (
        <div key={i.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 160, fontSize: 12, color: 'var(--text1)', flexShrink: 0,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {i.label}
          </div>
          <div style={{ flex: 1, background: 'var(--bg3)', borderRadius: 4, height: 8 }}>
            <div style={{
              width: `${(i.count / max) * 100}%`,
              height: '100%', background: color, borderRadius: 4,
              minWidth: i.count > 0 ? 6 : 0,
            }} />
          </div>
          <div style={{ fontSize: 12, color: 'var(--text)', width: 32, textAlign: 'right', fontWeight: 600 }}>
            {i.count}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text2)', width: 90, textAlign: 'right' }}>
            {fmt(i.amount)}
          </div>
        </div>
      ))}
    </div>
  );
}

const STAGE_NAMES = {
  NEW: 'Новый', WON: 'Успех ✓', LOSE: 'Провал',
  PREPARATION: 'В обработке', PREPAYMENT_INVOICE: 'Счёт выставлен',
  EXECUTING: 'Выполняется', FINAL_INVOICE: 'Финальный счёт',
};

// ─── Main CRM ────────────────────────────────────────────────────────────────
export default function CRM() {
  const [tab, setTab] = useState('pipelines');
  const [pipelines, setPipelines] = useState([]);
  const [selectedPipeline, setSelectedPipeline] = useState(null);
  const [loadingPipelines, setLoadingPipelines] = useState(false);

  const [deals, setDeals] = useState([]);
  const [leads, setLeads] = useState([]);
  const [leadsFunnel, setLeadsFunnel] = useState([]);

  const [tasks, setTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);

  const [chats, setChats] = useState([]);
  const [loadingChats, setLoadingChats] = useState(false);

  const totalUnreadChats = chats.reduce((s, c) => s + (c.unread || 0), 0);
  const overdueTasks = tasks.filter(t => {
    const d = daysDiff(t.deadline);
    return d !== null && d < 0;
  }).length;

  const loadPipelines = useCallback(() => {
    setLoadingPipelines(true);
    api.get('/crm/pipelines/db')
      .then(r => {
        setPipelines(r.data);
        if (r.data.length > 0 && !selectedPipeline) {
          setSelectedPipeline(r.data[0]);
        }
      })
      .catch(() => {})
      .finally(() => setLoadingPipelines(false));
  }, [selectedPipeline]);

  const loadTasks = useCallback(() => {
    setLoadingTasks(true);
    api.get('/crm/tasks').then(r => setTasks(r.data)).catch(() => {}).finally(() => setLoadingTasks(false));
  }, []);

  const loadChats = useCallback(() => {
    setLoadingChats(true);
    api.get('/crm/chats').then(r => setChats(r.data)).catch(() => {}).finally(() => setLoadingChats(false));
  }, []);

  useEffect(() => {
    loadPipelines();
    api.get('/crm/deals').then(r => setDeals(r.data)).catch(() => {});
    api.get('/crm/leads').then(r => setLeads(r.data)).catch(() => {});
    api.get('/crm/leads/funnel').then(r => setLeadsFunnel(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (tab === 'tasks' && !tasks.length) loadTasks();
    if (tab === 'chats' && !chats.length) loadChats();
  }, [tab]);

  // Build deals by pipeline from local data
  const dealsByPipeline = {};
  deals.forEach(d => {
    const cat = d.category_id || '0';
    if (!dealsByPipeline[cat]) dealsByPipeline[cat] = { count: 0, amount: 0 };
    dealsByPipeline[cat].count++;
    dealsByPipeline[cat].amount += d.amount || 0;
  });

  // Build leads funnel data
  const leadsChartData = leadsFunnel
    .filter(l => l.count > 0)
    .sort((a, b) => b.count - a.count)
    .slice(0, 8)
    .map(l => ({ name: (STAGE_NAMES[l.status] || l.status || 'Новый').slice(0, 18), count: l.count, amount: l.amount }));

  return (
    <div>
      <div className="kp-hero">
        <Plate>Битрикс24 · CRM</Plate>
        <h1 style={{ margin: 0 }}>CRM</h1>
        <p style={{ color: 'var(--text2)', margin: '8px 0 0', fontSize: 13 }}>
          Воронки, лиды, задачи и чаты
        </p>
      </div>

      {/* Summary metrics */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 24 }}>
        {[
          { label: 'Воронок', value: pipelines.length, icon: '🔀', color: 'var(--accent)' },
          { label: 'Открытых сделок', value: fmtNum(pipelines.reduce((s, p) => s + p.total_open, 0)), icon: '🤝', color: '#0e9f6e' },
          { label: 'Лидов', value: fmtNum(leads.length), icon: '🎯', color: '#00a7dc' },
          { label: 'Задач', value: tasks.length || '—', icon: '✅', color: overdueTasks > 0 ? '#e04848' : '#e08a00',
            sub: overdueTasks > 0 ? `${overdueTasks} просрочено` : null },
          { label: 'Чатов', value: chats.length || '—', icon: '💬', color: totalUnreadChats > 0 ? '#e04848' : 'var(--accent)',
            sub: totalUnreadChats > 0 ? `${totalUnreadChats} непрочитано` : null },
        ].map(m => (
          <div key={m.label} style={{
            background: 'var(--bg2)',
            borderRadius: 12,
            padding: '14px 16px',
            border: '1px solid var(--border)',
          }}>
            <div style={{ fontSize: 18, marginBottom: 6 }}>{m.icon}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: m.color }}>{m.value}</div>
            <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>{m.label}</div>
            {m.sub && <div style={{ fontSize: 11, color: '#e04848', marginTop: 2, fontWeight: 600 }}>{m.sub}</div>}
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        <Tab label="🔀 Воронки" active={tab === 'pipelines'} onClick={() => setTab('pipelines')} />
        <Tab label="🎯 Лиды" active={tab === 'leads'} badge={leads.length} onClick={() => setTab('leads')} />
        <Tab label="🤝 Сделки" active={tab === 'deals'} onClick={() => setTab('deals')} />
        <Tab label="✅ Задачи" active={tab === 'tasks'}
          badge={overdueTasks > 0 ? overdueTasks : 0}
          onClick={() => setTab('tasks')} />
        <Tab label="💬 Чаты" active={tab === 'chats'}
          badge={totalUnreadChats}
          onClick={() => { setTab('chats'); if (!chats.length) loadChats(); }} />
      </div>

      {/* ── Pipelines tab ── */}
      {tab === 'pipelines' && (
        <div>
          {/* Toolbar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <div style={{ color: 'var(--text2)', fontSize: 13 }}>
              Данные из локальной базы · обновляются каждые 2 часа
            </div>
            <button
              onClick={loadPipelines}
              disabled={loadingPipelines}
              style={{
                marginLeft: 'auto',
                background: 'var(--bg2)',
                border: '1px solid var(--border)',
                borderRadius: 8,
                padding: '7px 14px',
                color: 'var(--text1)',
                cursor: 'pointer',
                fontSize: 13,
                opacity: loadingPipelines ? 0.5 : 1,
              }}>
              🔄 Обновить
            </button>
          </div>

          {loadingPipelines ? (
            <div style={{ textAlign: 'center', padding: 60, color: 'var(--text2)' }}>
              Загружаем воронки…
            </div>
          ) : pipelines.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60, color: 'var(--text2)' }}>
              Нет данных. Нажмите «Обновить» или проверьте синхронизацию.
            </div>
          ) : (
            <>
              {/* Total across all pipelines */}
              <div style={{
                display: 'flex',
                gap: 16,
                marginBottom: 18,
                flexWrap: 'wrap',
              }}>
                {[
                  {
                    label: 'Всего воронок',
                    value: pipelines.length,
                    color: 'var(--accent)',
                  },
                  {
                    label: 'Открытых сделок',
                    value: fmtNum(pipelines.reduce((s, p) => s + p.total_open, 0)),
                    color: '#0e9f6e',
                  },
                  {
                    label: 'Сумма (открытые)',
                    value: fmt(pipelines.reduce((s, p) => s + p.total_amount, 0)),
                    color: '#e08a00',
                  },
                ].map(m => (
                  <div key={m.label} style={{
                    background: 'var(--bg2)',
                    borderRadius: 10,
                    padding: '10px 18px',
                    border: '1px solid var(--border)',
                  }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: m.color }}>{m.value}</div>
                    <div style={{ fontSize: 12, color: 'var(--text2)' }}>{m.label}</div>
                  </div>
                ))}
              </div>

              {/* Pipeline cards grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(310px, 1fr))',
                gap: 14,
                marginBottom: 20,
              }}>
                {pipelines.map(p => (
                  <PipelineCard
                    key={p.id}
                    pipeline={p}
                    active={selectedPipeline?.id === p.id}
                    onClick={() => setSelectedPipeline(p === selectedPipeline ? null : p)}
                  />
                ))}
              </div>

              {/* Detailed funnel for selected pipeline */}
              {selectedPipeline && (
                <PipelineDetail pipeline={selectedPipeline} />
              )}

              {/* All deals table grouped by pipeline */}
              <div style={{ marginTop: 24 }}>
                <h3 style={{ margin: '0 0 14px', fontSize: 15, color: 'var(--text)' }}>
                  📋 Все сделки по воронкам
                </h3>
                {pipelines.map(p => (
                  <div key={p.id} style={{ marginBottom: 20 }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '10px 14px',
                      background: 'var(--bg2)',
                      borderRadius: '10px 10px 0 0',
                      borderBottom: `3px solid ${p.color}`,
                    }}>
                      <span style={{ fontSize: 20 }}>{p.icon}</span>
                      <span style={{ fontWeight: 700, color: 'var(--text)', fontSize: 14 }}>
                        {p.name}
                      </span>
                      <span style={{ marginLeft: 'auto', fontWeight: 700, color: p.color, fontSize: 14 }}>
                        {fmtNum(p.total_open)} сделок · {fmt(p.total_amount)}
                      </span>
                    </div>
                    <div style={{
                      background: 'var(--bg2)',
                      borderRadius: '0 0 10px 10px',
                      border: '1px solid var(--border)',
                      borderTop: 'none',
                      overflow: 'hidden',
                    }}>
                      {/* Stage rows */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr auto auto',
                        gap: 0,
                      }}>
                        {p.stages.map((s, idx) => (
                          <React.Fragment key={s.stage_id}>
                            <div style={{
                              padding: '8px 14px',
                              borderBottom: idx < p.stages.length - 1 ? '1px solid var(--border)' : 'none',
                              fontSize: 13,
                              color: 'var(--text1)',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                            }}>
                              <div style={{
                                width: `${Math.round((s.count / Math.max(...p.stages.map(x => x.count), 1)) * 120)}px`,
                                maxWidth: 120,
                                minWidth: s.count > 0 ? 4 : 0,
                                height: 6,
                                background: p.color,
                                borderRadius: 3,
                                opacity: 0.6,
                                flexShrink: 0,
                              }} />
                              {s.name}
                            </div>
                            <div style={{
                              padding: '8px 14px',
                              borderBottom: idx < p.stages.length - 1 ? '1px solid var(--border)' : 'none',
                              fontSize: 13,
                              fontWeight: 700,
                              color: p.color,
                              textAlign: 'right',
                              whiteSpace: 'nowrap',
                            }}>
                              {fmtNum(s.count)}
                            </div>
                            <div style={{
                              padding: '8px 14px',
                              borderBottom: idx < p.stages.length - 1 ? '1px solid var(--border)' : 'none',
                              fontSize: 12,
                              color: 'var(--text2)',
                              textAlign: 'right',
                              whiteSpace: 'nowrap',
                            }}>
                              {fmt(s.amount)}
                            </div>
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Leads tab ── */}
      {tab === 'leads' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div style={{ background: 'var(--bg2)', borderRadius: 14, padding: 20, border: '1px solid var(--border)' }}>
            <h3 style={{ margin: '0 0 16px', color: 'var(--text)', fontSize: 15 }}>📊 Воронка лидов</h3>
            {leadsChartData.length > 0 ? (
              <div style={{ height: 260 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={leadsChartData} layout="vertical">
                    <XAxis type="number" tick={{ fill: 'var(--text2)', fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" width={120} tick={{ fill: 'var(--text1)', fontSize: 11 }} />
                    <Tooltip
                      contentStyle={{ background: 'var(--bg3)', border: '1px solid #00a7dc44', borderRadius: 8, color: 'var(--text)' }}
                    />
                    <Bar dataKey="count" fill="#00a7dc" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div style={{ color: 'var(--text2)', textAlign: 'center', padding: 40 }}>Нет данных</div>
            )}
          </div>
          <div style={{ background: 'var(--bg2)', borderRadius: 14, padding: 20, border: '1px solid var(--border)' }}>
            <h3 style={{ margin: '0 0 16px', color: 'var(--text)', fontSize: 15 }}>
              🎯 Лиды — <span style={{ color: '#00a7dc' }}>{leads.length}</span> открытых
            </h3>
            <div style={{ overflowY: 'auto', maxHeight: 300 }}>
              {leads.slice(0, 30).map(l => (
                <div key={l.id} style={{
                  padding: '8px 0',
                  borderBottom: '1px solid var(--border)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 8,
                }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {l.name || l.title || 'Без имени'}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text2)' }}>{l.status}</div>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#00a7dc', flexShrink: 0 }}>
                    {fmt(l.amount)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Deals tab ── */}
      {tab === 'deals' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div style={{ background: 'var(--bg2)', borderRadius: 14, padding: 20, border: '1px solid var(--border)' }}>
            <h3 style={{ margin: '0 0 16px', color: 'var(--text)', fontSize: 15 }}>📊 Сделки по стадиям</h3>
            {deals.length > 0 ? (
              <div style={{ height: 260 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={
                    Object.entries(deals.reduce((acc, d) => {
                      const st = STAGE_NAMES[d.stage] || d.stage || 'Новый';
                      if (!acc[st]) acc[st] = { count: 0, amount: 0 };
                      acc[st].count++;
                      acc[st].amount += d.amount || 0;
                      return acc;
                    }, {}))
                    .map(([name, v]) => ({ name: name.slice(0, 16), ...v }))
                    .sort((a, b) => b.count - a.count)
                    .slice(0, 8)
                  } layout="vertical">
                    <XAxis type="number" tick={{ fill: 'var(--text2)', fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" width={120} tick={{ fill: 'var(--text1)', fontSize: 11 }} />
                    <Tooltip
                      contentStyle={{ background: 'var(--bg3)', border: '1px solid #0e9f6e44', borderRadius: 8, color: 'var(--text)' }}
                    />
                    <Bar dataKey="count" fill="#0e9f6e" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div style={{ color: 'var(--text2)', textAlign: 'center', padding: 40 }}>Нет данных</div>
            )}
          </div>
          <div style={{ background: 'var(--bg2)', borderRadius: 14, padding: 20, border: '1px solid var(--border)' }}>
            <h3 style={{ margin: '0 0 12px', color: 'var(--text)', fontSize: 15 }}>
              🤝 Последние сделки
            </h3>
            <div style={{ overflowY: 'auto', maxHeight: 300 }}>
              {deals.slice(0, 30).map(d => (
                <div key={d.id} style={{
                  padding: '8px 0',
                  borderBottom: '1px solid var(--border)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 8,
                }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {d.title}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text2)' }}>
                      {STAGE_NAMES[d.stage] || d.stage} · {d.responsible}
                    </div>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#0e9f6e', flexShrink: 0 }}>
                    {fmt(d.amount)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Tasks tab ── */}
      {tab === 'tasks' && (
        <div>
          {loadingTasks ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text2)' }}>
              Загружаем задачи…
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                {[
                  { label: 'Всего задач', value: tasks.length, color: 'var(--accent)' },
                  { label: 'Просрочено', value: overdueTasks, color: '#e04848' },
                  { label: 'Срочных (≤2д)', value: tasks.filter(t => { const d = daysDiff(t.deadline); return d !== null && d >= 0 && d <= 2; }).length, color: '#e08a00' },
                ].map(m => (
                  <div key={m.label} style={{
                    background: 'var(--bg2)',
                    borderRadius: 10,
                    padding: '10px 16px',
                    border: '1px solid var(--border)',
                    minWidth: 110,
                  }}>
                    <div style={{ fontSize: 20, fontWeight: 800, color: m.color }}>{m.value}</div>
                    <div style={{ fontSize: 12, color: 'var(--text2)' }}>{m.label}</div>
                  </div>
                ))}
                <button
                  onClick={loadTasks}
                  style={{
                    marginLeft: 'auto',
                    background: 'var(--bg2)',
                    border: '1px solid var(--border)',
                    borderRadius: 8,
                    padding: '8px 14px',
                    color: 'var(--text1)',
                    cursor: 'pointer',
                    fontSize: 13,
                  }}>
                  🔄 Обновить
                </button>
              </div>
              <TasksTab tasks={tasks} />
            </>
          )}
        </div>
      )}

      {/* ── Chats tab ── */}
      {tab === 'chats' && (
        <div>
          {loadingChats ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text2)' }}>
              Загружаем чаты…
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                <button
                  onClick={loadChats}
                  style={{
                    background: 'var(--bg2)',
                    border: '1px solid var(--border)',
                    borderRadius: 8,
                    padding: '8px 14px',
                    color: 'var(--text1)',
                    cursor: 'pointer',
                    fontSize: 13,
                  }}>
                  🔄 Обновить
                </button>
              </div>
              <ChatsTab chats={chats} />
            </>
          )}
        </div>
      )}
    </div>
  );
}
